import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

const leafSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.9C15.5 4.9 17 3.5 19 1c1 2 2 4.5 2 8 0 5.5-4.78 11-10 11Z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></svg>';

export const freshNature: TemplateRegistration = {
  meta: {
    id: 'fresh-nature',
    name: 'Fresh Nature',
    category: 'Minimal',
    description: 'Organic nature-inspired design with earthy greens and warm earth tones',
    previewImage: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1600&q=80';
    const aboutImage = photos[1] || null;
    const galleryImages = photos.slice(2, 7);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Quicksand', 'Nunito']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="fn-reviews">
        <div class="fn-container">
          <div class="fn-review-pill">
            <span class="fn-leaf-icon">${leafSvg}</span>
            <div class="fn-review-stars">${renderStars(Math.round(rating))}</div>
            <p>${rating.toFixed(1)} &bull; ${reviewCount} reviews</p>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="fn-promo">
        <div class="fn-container">
          <div class="fn-promo-card">
            <span class="fn-promo-badge">${promoDiscount}</span>
            <div class="fn-promo-text">
              <strong>${promoTitle}</strong>
              <p>${promoDesc}</p>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="fn-section">
        <div class="fn-container">
          <div class="fn-about-wrap">
            <div class="fn-about-content">
              <span class="fn-tag">${leafSvg} About Us</span>
              <h2>${safeName}</h2>
              <p>${safeAbout}</p>
            </div>
            ${aboutImage ? `<div class="fn-about-img-wrap"><img src="${escapeAttr(aboutImage)}" alt="${safeName}" loading="lazy"></div>` : ''}
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const cards = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="fn-service-card">
            <div class="fn-service-icon">${getIconSvg(s.icon || 'heart')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="fn-service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="fn-section fn-section-tinted">
        <div class="fn-container">
          <span class="fn-tag">${leafSvg} What We Do</span>
          <h2 class="fn-section-title">Our Services</h2>
          <div class="fn-services-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url, i) => {
        const radiuses = ['60% 40% 50% 50%', '40% 60% 45% 55%', '50% 50% 60% 40%', '55% 45% 40% 60%', '45% 55% 55% 45%'];
        const r = radiuses[i % radiuses.length];
        return `<div class="fn-gallery-item" style="border-radius:${r};"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="fn-section">
        <div class="fn-container">
          <span class="fn-tag">${leafSvg} Gallery</span>
          <div class="fn-gallery-flow">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const items = data.testimonials.map((t, i) => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          ${i > 0 ? `<div class="fn-leaf-divider">${leafSvg}</div>` : ''}
          <div class="fn-testimonial">
            <p class="fn-testimonial-text">&ldquo;${tContent}&rdquo;</p>
            <div class="fn-testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="fn-t-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="fn-section fn-section-tinted">
        <div class="fn-container">
          <span class="fn-tag">${leafSvg} Testimonials</span>
          <h2 class="fn-section-title">What People Say</h2>
          <div class="fn-testimonials-list">${items}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const faqItems = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="fn-faq-item">
            <h3>${q}</h3>
            <p>${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="fn-section">
        <div class="fn-container">
          <span class="fn-tag">${leafSvg} FAQ</span>
          <h2 class="fn-section-title">Common Questions</h2>
          <div class="fn-faq-list">${faqItems}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="fn-price-row">
            <div class="fn-price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="fn-price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="fn-section fn-section-tinted">
        <div class="fn-container">
          <span class="fn-tag">${leafSvg} Pricing</span>
          <h2 class="fn-section-title">Our Pricing</h2>
          <div class="fn-price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="fn-section">
        <div class="fn-container">
          <span class="fn-tag">${leafSvg} Hours</span>
          <ul class="fn-hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="fn-section fn-section-tinted">
        <div class="fn-container">
          <span class="fn-tag">${leafSvg} Contact</span>
          <h2 class="fn-section-title">Get in Touch</h2>
          <div class="fn-contact-layout">
            <div class="fn-contact-info">

              ${enabledFeatures.has('phone-display') && phone ? `<div class="fn-contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="fn-contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="fn-map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">Open in Maps &rarr;</a>` : ''}
            </div>
            <div class="fn-contact-accent-bar"></div>
            <form class="fn-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Name" required>
              <input type="email" placeholder="Email" required>
              <textarea placeholder="Your message..." rows="5" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #f0fdf4; --bg-tint: #dcfce7; --green: #16a34a; --green-dark: #14532d; --green-light: #bbf7d0;
      --earth: #a16207; --earth-light: #fef3c7; --text: #14532d; --text-muted: #365e3d;
      --font-heading: 'Quicksand', sans-serif; --font-body: 'Nunito', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.75; font-size: 17px; }
    .fn-container { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--green); text-decoration: none; transition: color .2s; }
    a:hover { color: var(--earth); }
    img { max-width: 100%; display: block; }

    /* NAV */
    .fn-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(240,253,244,.95); backdrop-filter: blur(12px); border-bottom: 1px solid var(--green-light); }
    .fn-nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 1100px; margin: 0 auto; }
    .fn-nav-brand { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 700; color: var(--green-dark); display: flex; align-items: center; gap: 6px; }
    .fn-nav-brand svg { color: var(--green); width: 18px; height: 18px; }
    .fn-nav-links { display: flex; gap: 24px; list-style: none; }
    .fn-nav-links a { color: var(--text-muted); font-family: var(--font-heading); font-size: .85rem; font-weight: 600; transition: color .2s; }
    .fn-nav-links a:hover { color: var(--green); }
    .fn-nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .fn-nav-toggle span { display: block; width: 22px; height: 2px; background: var(--green-dark); margin: 5px 0; border-radius: 2px; }

    /* HERO */
    .fn-hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; background: linear-gradient(160deg, var(--bg) 0%, #dcfce7 40%, #d9f99d 100%); }
    .fn-hero-content { position: relative; z-index: 2; max-width: 1100px; margin: 0 auto; width: 100%; padding: 120px 24px 80px; display: grid; grid-template-columns: 1fr 1fr; gap: 0; align-items: stretch; min-height: 80vh; }
    .fn-hero-text { display: flex; flex-direction: column; justify-content: center; padding: 48px 40px 48px 0; }
    .fn-hero-text h1 { font-family: var(--font-heading); font-size: clamp(2rem, 4.5vw, 3.4rem); font-weight: 700; color: var(--green-dark); line-height: 1.2; margin-bottom: 20px; }
    .fn-hero-text h1 .fn-leaf-inline { display: inline-block; color: var(--green); vertical-align: middle; margin: 0 4px; }
    .fn-hero-text h1 .fn-leaf-inline svg { width: 32px; height: 32px; }
    .fn-hero-sub { font-size: 1.1rem; color: var(--text-muted); max-width: 460px; margin-bottom: 32px; line-height: 1.8; }
    .fn-hero-cta { display: inline-block; padding: 14px 40px; background: var(--green); color: #fff; font-family: var(--font-heading); font-size: .9rem; font-weight: 700; border-radius: 50px; transition: background .3s, transform .2s; }
    .fn-hero-cta:hover { background: var(--green-dark); color: #fff; transform: translateY(-2px); }
    .fn-hero-image { position: relative; clip-path: polygon(15% 0, 100% 0, 100% 100%, 0 100%); }
    .fn-hero-image img { width: 100%; height: 100%; min-height: 480px; object-fit: cover; display: block; }

    /* SECTIONS */
    .fn-section { padding: 88px 0; }
    .fn-section-tinted { background: var(--bg-tint); }
    .fn-tag { display: inline-flex; align-items: center; gap: 6px; font-family: var(--font-heading); font-size: .8rem; font-weight: 700; color: var(--green); text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 12px; }
    .fn-tag svg { width: 16px; height: 16px; }
    .fn-section-title { font-family: var(--font-heading); font-size: clamp(1.5rem, 3vw, 2.2rem); font-weight: 700; color: var(--green-dark); margin-bottom: 40px; }

    /* ABOUT */
    .fn-about-wrap { display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: center; }
    .fn-about-img-wrap img { width: 100%; height: 400px; object-fit: cover; border-radius: 12px; box-shadow: 0 16px 60px rgba(22,163,74,.15); }
    .fn-about-content h2 { font-family: var(--font-heading); font-size: 1.8rem; font-weight: 700; color: var(--green-dark); margin-bottom: 16px; }
    .fn-about-content p { color: var(--text-muted); line-height: 1.9; font-size: 1.02rem; }

    /* SERVICES */
    .fn-services-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
    .fn-service-card { background: #fff; border-radius: 16px; padding: 32px 24px; box-shadow: 0 4px 24px rgba(20,83,45,.06); transition: transform .3s, box-shadow .3s; border-left: 4px solid var(--green); }
    .fn-service-card:hover { transform: translateY(-4px); box-shadow: 0 8px 32px rgba(20,83,45,.12); }
    .fn-service-icon { width: 52px; height: 52px; background: var(--green-light); border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; color: var(--green); }
    .fn-service-icon .icon-char { font-size: 24px; line-height: 1; }
    .fn-service-card h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; color: var(--green-dark); margin-bottom: 8px; }
    .fn-service-card p { color: var(--text-muted); font-size: .93rem; line-height: 1.7; }
    .fn-service-price { display: inline-block; margin-top: 12px; color: var(--earth); font-weight: 700; font-size: .95rem; }

    /* GALLERY */
    .fn-gallery-flow { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; }
    .fn-gallery-item { overflow: hidden; box-shadow: 0 4px 20px rgba(20,83,45,.1); }
    .fn-gallery-item img { width: 100%; height: 260px; object-fit: cover; transition: transform .4s; }
    .fn-gallery-item:hover img { transform: scale(1.05); }

    /* TESTIMONIALS */
    .fn-testimonials-list { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .fn-leaf-divider { display: none; }
    .fn-testimonial { background: #fff; border-radius: 16px; padding: 28px 24px; box-shadow: 0 4px 20px rgba(20,83,45,.06); text-align: center; }
    .fn-testimonial-text { font-size: 1rem; font-style: italic; color: var(--text-muted); line-height: 1.85; margin-bottom: 16px; }
    .fn-testimonial-author strong { display: block; font-family: var(--font-heading); font-weight: 700; color: var(--green-dark); margin-bottom: 2px; }
    .fn-testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .fn-t-stars { margin-top: 6px; }
    .star { color: #d1d5db; font-size: .95rem; }
    .star.filled { color: var(--earth); }

    /* GOOGLE REVIEWS */
    .fn-reviews { padding: 24px 0; border-bottom: 1px solid var(--green-light); }
    .fn-review-pill { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .fn-review-pill p { color: var(--text-muted); font-size: .88rem; }
    .fn-leaf-icon .icon-char { font-size: 18px; line-height: 1; color: var(--green); }

    /* PROMO */
    .fn-promo { padding: 20px 0; background: var(--green); }
    .fn-promo-card { display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap; }
    .fn-promo-badge { background: #fff; color: var(--green); font-family: var(--font-heading); font-weight: 800; padding: 6px 16px; border-radius: 50px; font-size: .9rem; }
    .fn-promo-text strong { color: #fff; font-family: var(--font-heading); }
    .fn-promo-text p { color: rgba(255,255,255,.85); font-size: .85rem; }

    /* FAQ */
    .fn-faq-list { max-width: 700px; }
    .fn-faq-item { padding: 24px 0; border-bottom: 1px solid var(--green-light); }
    .fn-faq-item h3 { font-family: var(--font-heading); font-weight: 700; font-size: 1rem; color: var(--green-dark); margin-bottom: 8px; }
    .fn-faq-item p { color: var(--text-muted); font-size: .95rem; line-height: 1.8; }

    /* PRICE LIST */
    .fn-price-list { max-width: 700px; }
    .fn-price-row { display: flex; align-items: center; justify-content: space-between; padding: 20px 0; border-bottom: 1px solid var(--green-light); gap: 16px; }
    .fn-price-info h3 { font-family: var(--font-heading); font-weight: 700; font-size: .95rem; color: var(--green-dark); margin-bottom: 2px; }
    .fn-price-info p { color: var(--text-muted); font-size: .85rem; }
    .fn-price-amount { font-family: var(--font-heading); font-weight: 800; font-size: 1.2rem; color: var(--earth); white-space: nowrap; }

    /* HOURS */
    .fn-hours-list { list-style: none; max-width: 400px; }
    .fn-hours-list li { padding: 12px 0; border-bottom: 1px solid var(--green-light); color: var(--text-muted); font-size: .95rem; }

    /* CONTACT */
    .fn-contact-layout { display: grid; grid-template-columns: 1fr 4px 1fr; gap: 40px; align-items: start; }
    .fn-contact-accent-bar { background: var(--green); border-radius: 2px; align-self: stretch; }
    .fn-contact-info { display: flex; flex-direction: column; gap: 18px; }
    .fn-contact-item { display: flex; align-items: center; gap: 10px; color: var(--text-muted); }
    .fn-contact-item .icon-char { color: var(--green); font-size: 20px; line-height: 1; flex-shrink: 0; }
    .fn-contact-item a { color: var(--green-dark); font-weight: 600; }
    .fn-map-link { display: inline-block; margin-top: 8px; font-family: var(--font-heading); font-weight: 700; font-size: .85rem; }
    .fn-form { display: flex; flex-direction: column; gap: 14px; }
    .fn-form input, .fn-form textarea { background: #fff; border: 1px solid var(--green-light); color: var(--text); padding: 14px 18px; font-family: var(--font-body); font-size: 1rem; border-radius: 12px; outline: none; transition: border-color .2s, box-shadow .2s; }
    .fn-form input:focus, .fn-form textarea:focus { border-color: var(--green); box-shadow: 0 0 0 3px rgba(22,163,74,.12); }
    .fn-form input::placeholder, .fn-form textarea::placeholder { color: #93a89a; }
    .fn-form button { padding: 14px; background: var(--green); color: #fff; border: none; font-family: var(--font-heading); font-size: .9rem; font-weight: 700; border-radius: 50px; cursor: pointer; transition: background .3s; }
    .fn-form button:hover { background: var(--green-dark); }

    /* FOOTER */
    .fn-footer { padding: 48px 0; text-align: center; border-top: 1px solid var(--green-light); background: var(--bg); }
    .fn-footer-brand { display: flex; align-items: center; justify-content: center; gap: 6px; font-family: var(--font-heading); font-size: 1rem; font-weight: 700; color: var(--green-dark); margin-bottom: 12px; }
    .fn-footer-brand svg { color: var(--green); width: 16px; height: 16px; }
    .fn-footer p { color: var(--text-muted); font-size: .82rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .fn-nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(240,253,244,.98); padding: 16px 24px; gap: 16px; }
      .fn-nav-links.show { display: flex; }
      .fn-nav-toggle { display: block; }
      .fn-hero-content { grid-template-columns: 1fr; text-align: center; }
      .fn-hero-text { padding: 48px 0; }
      .fn-hero-sub { margin: 0 auto 32px; }
      .fn-hero-image { clip-path: none; }
      .fn-hero-image img { min-height: 320px; height: 320px; }
      .fn-about-wrap { grid-template-columns: 1fr; gap: 32px; }
      .fn-contact-layout { grid-template-columns: 1fr; gap: 32px; }
      .fn-contact-accent-bar { display: none; }
      .fn-services-grid { grid-template-columns: 1fr; }
      .fn-testimonials-list { grid-template-columns: 1fr; }
    }
    @media (max-width: 480px) {
      .fn-section { padding: 56px 0; }
      .fn-gallery-flow { grid-template-columns: repeat(2, 1fr); gap: 12px; }
      .fn-price-row { flex-direction: column; align-items: flex-start; gap: 8px; }
    }
  </style>
</head>
<body>
  <nav class="fn-nav">
    <div class="fn-nav-inner">
      <a href="#" class="fn-nav-brand">${leafSvg} ${safeName}</a>
      <button class="fn-nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.fn-nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="fn-nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="fn-hero">
    <div class="fn-hero-content">
      <div class="fn-hero-text">
        <h1>${safeHeadline} <span class="fn-leaf-inline">${leafSvg}</span></h1>
        <p class="fn-hero-sub">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="fn-hero-cta">Get Started</a>` : ''}
      </div>
      <div class="fn-hero-image">
        <img src="${escapeAttr(heroImage)}" alt="${safeName}">
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="fn-footer">
    <div class="fn-container">
      <div class="fn-footer-brand">${leafSvg} ${safeName}</div>
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
